import { colors } from "../../lib/colors"
import { useState, useEffect } from "react"
import { api } from "../../lib/api"
import { useSelectedAccount } from "../../contexts/AccountContext"
import type { Category } from "../../types/analytics"

type Props = {
  postId: number
  accountId: number
  initialCategoryId: number | null
  initialCategoryName: string | null
  readOnly?: boolean
}

const PostCategorySelector = ({ postId, accountId, initialCategoryId, initialCategoryName, readOnly = false }: Props) => {
  const { organizations } = useSelectedAccount()
  const orgId = organizations.find(o => o.accounts.some(a => a.id === accountId))?.id
  const [categories, setCategories]     = useState<Category[]>([])
  const [categoryId, setCategoryId]     = useState<number | null>(initialCategoryId)
  const [categoryName, setCategoryName] = useState<string | null>(initialCategoryName)
  const [showNewInput, setShowNewInput] = useState(false)
  const [newName, setNewName]           = useState("")
  const [saving, setSaving]             = useState(false)

  useEffect(() => {
    if (readOnly) return
    const params = orgId != null ? `?organization_id=${orgId}` : ""
    api.get<Category[]>(`/instagram_accounts/${accountId}/categories${params}`)
      .then(setCategories)
      .catch(() => {})
  }, [accountId, readOnly, orgId])

  const handleChange = async (value: string) => {
    if (value === "__new__") { setShowNewInput(true); return }
    setSaving(true)
    try {
      const res = await api.patch<{ category_id: number | null; category_name: string | null }>(
        `/analytics/posts/${postId}/category`,
        { category_id: value || null, organization_id: orgId }
      )
      setCategoryId(res.category_id)
      setCategoryName(res.category_name)
    } catch {
    } finally {
      setSaving(false)
    }
  }

  const handleCreate = async () => {
    if (!newName.trim()) return
    setSaving(true)
    try {
      const res = await api.post<{ id: number; name: string; category_id: number; category_name: string }>(
        `/analytics/posts/${postId}/category`,
        { name: newName.trim(), organization_id: orgId }
      )
      setCategories(prev => [...prev, { id: res.id, name: res.name, posts_count: 1 }])
      setCategoryId(res.category_id)
      setCategoryName(res.category_name)
      setShowNewInput(false)
      setNewName("")
    } catch {
    } finally {
      setSaving(false)
    }
  }

  if (readOnly) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 13, color: "#6b7280" }}>カテゴリ:</span>
        <span style={{ fontSize: 13 }}>{initialCategoryName ?? "カテゴリなし"}</span>
      </div>
    )
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 13, color: "#6b7280" }}>カテゴリ:</span>
      {!showNewInput ? (
        <select
          className="form-input"
          style={{ width: "auto", minWidth: 160, fontSize: 13 }}
          value={categoryId ?? ""}
          onChange={e => handleChange(e.target.value)}
          disabled={saving}
        >
          <option value="">カテゴリなし</option>
          {categories.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
          <option value="__new__">+ 新規作成...</option>
        </select>
      ) : (
        <>
          <input
            className="form-input"
            style={{ width: 160, fontSize: 13 }}
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="カテゴリ名"
            autoFocus
          />
          <button className="ps-btn ps-btn--primary" onClick={handleCreate} disabled={saving || !newName.trim()}>
            {saving ? "..." : "作成"}
          </button>
          <button className="ps-btn" onClick={() => { setShowNewInput(false); setNewName("") }}>
            キャンセル
          </button>
        </>
      )}
      {categoryName && !showNewInput && (
        <span style={{ fontSize: 12, color: colors.gray400 }}>現在: {categoryName}</span>
      )}
    </div>
  )
}

export default PostCategorySelector
